import React, { Component, createRef, RefObject } from 'react';
import ReactDOM from 'react-dom';
import { breakpoints } from '@rocheglobal/component-library-dot-com/src/style/base';
import { ExternalDataService } from '../../services/external-data';
import { FilterProps, FilterService, PipelineEntry } from '../../services/filter';
import { Props } from '../types';

export interface RocheSelectItem {
  label: string;
  value: string;
  group?: string;
  checked?: boolean;
}
export const cardiovascularOldLabel = 'Cardiovascular & Metabolism';

export const cardiovascularNewLabel = 'Cardiovascular, Renal & Metabolism';
const cardiovascularKey = 'cardiovascular & metabolism';
/**
 * TODO: implement translation Service
 */
const translations = {
  oncology: 'Oncology',
  'oncology/hematology': 'Oncology/Hematology',
  immunology: 'Immunology',
  cardiovascular: cardiovascularNewLabel,
  'cardiovascular & metabolism': cardiovascularNewLabel,
  ophthalmology: 'Ophthalmology',
  'infectious-diseases': 'Infectious Diseases',
  neuroscience: 'Neuroscience',
  virology: 'Virology',
  other: 'Other',
  ai: 'Additional Indication',
  nme: 'New Molecular Entity',
  one: 'Phase 1',
  tow: 'Phase 2',
  three: 'Phase 3',
  four: 'Phase 4',
  chugai: 'chugai',
  'roche-late-stage-product-development': 'Roche Late Stage Product Development',
  'genentech-research-and-early-development': 'Genentech Research and Early Development',
  'pharma-research-and-early-development': 'Pharma Research and Early Development',
  spark: 'spark',
  'partner-managed': 'Partner Managed',
};

const groupListByOptions = [
  {
    label: 'All',
    value: 'all',
  },
  {
    label: 'Therapeutic Area',
    value: 'therapeuticAreaGroupByValue',
  },
  {
    label: 'Partner',
    value: 'partner',
  },
  {
    label: 'Phase',
    value: 'phase',
  },
  {
    label: 'Managed By',
    value: 'managedBy',
  },
  {
    label: 'Expected Filing',
    value: 'filingDate',
  },
];

export enum FilterGroupType {
  THERAPEUTIC_AREA = 'therapeuticArea',
  THERAPEUTIC_AREA_FILTER_VALUE = 'therapeuticAreaFilterValue',
  PROJECT_TYPE = 'projectType',
  PHASE = 'phase',
  EXPECTED_FILING_DATE = 'filingDate',
  MANAGED_BY = 'managedBy',
}

export enum ProjectType {
  NEW_MOLECULAR_ENTITY = 'nme',
  ADDITIONAL_INDICATOR = 'ai',
}

export enum TypeNames {
  SEARCH = 'search',
  GROUP_LIST_BY = 'groupListBy',
}

export enum Phases {
  PHASE_ONE = 'Phase 1',
  PHASE_TWO = 'Phase 2',
  PHASE_THREE = 'Phase 3',
  FILED = 'Filed',
  APPROVED = 'Approved',
}

interface FiltersOptions {
  therapeuticAreaFilterValue: RocheSelectItem[],
  projectTypes: RocheSelectItem[],
  phases: RocheSelectItem[],
  filingDates: RocheSelectItem[],
  managedBy: RocheSelectItem[],
}

interface PipelineState {
  content: PipelineEntry[];
  isModalOpen: boolean;
  filters: Map<string, FilterProps>;
  searchText: string;
  groupListBy: string;
  isMedium: boolean;
  isSmall: boolean;
  filterOptions: FiltersOptions;
}

const moveEntryToEnd = (
  pipelineEntries: RocheSelectItem[],
  value: string,
): RocheSelectItem[] => pipelineEntries.sort((item: RocheSelectItem) => {
  if (item.value !== value) {
    return 0;
  }
  return 1;
});

const getOptionsByKey = (pipelineEntries: PipelineEntry[], key: keyof PipelineEntry):
RocheSelectItem[] => {
  const names = pipelineEntries?.map(
    (entry: PipelineEntry) => entry[key],
  ).filter(
    (entry, index, self) => index === self.indexOf(entry),
  );
  return names?.map((name) => ({
    group: key,
    label: translations[name] ? translations[name] : name,
    value: name,
  }))?.sort((left, right) => (left?.label?.toLowerCase() > right?.label?.toLowerCase() ? 1 : -1))
    ?.filter((element) => element?.label);
};

// build time (node context) does not have document defined.
const modal = typeof document !== 'undefined' ? document.getElementById('global-modal') : null;

export class RochePipeline extends Component<Props, PipelineState> {
  private container: RefObject<HTMLDivElement>;

  private originalContent: PipelineEntry[];

  constructor(props) {
    super(props);
    this.handleFiltersUpdate = this.handleFiltersUpdate.bind(this);
    this.handleModalTrigger = this.handleModalTrigger.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.state = {
      content: this.originalContent,
      isModalOpen: false,
      filters: new Map<string, FilterProps>(),
      searchText: '',
      groupListBy: 'all',
      isMedium: this.isMedium,
      isSmall: this.isSmall,
      filterOptions: {
        therapeuticAreaFilterValue: [],
        projectTypes: [],
        phases: [],
        filingDates: [],
        managedBy: [],
      },
    };

    this.container = createRef();
  }

  public componentDidMount(): void {
    ExternalDataService.getPipeline()
      .then((response: { Items: PipelineEntry[] }) => {
        this.originalContent = response.Items.map((item) => this.cleanupPipelineEntry(item));
        this.setState({ content: this.originalContent });
        this.updateFilterOptions(this.originalContent);
      });

    this.container.current.addEventListener('rocheFormControlChange', this.handleFiltersUpdate);
    modal.addEventListener('rocheFormControlChange', this.handleFiltersUpdate);
    window.addEventListener('rocheModalTrigger', this.handleModalTrigger);
    window.addEventListener('rocheModalClose', this.handleModalClose);
    window.addEventListener('resize', this.handleResize);
  }

  public render(): JSX.Element {
    const {
      content, isModalOpen, isMedium, groupListBy, isSmall,
    } = this.state;

    return (
      <div ref={this.container}>
        {isModalOpen && ReactDOM.createPortal(
          <roche-grid>
            <roche-headline
              headline="Filter"
              level={2}
            ></roche-headline>
            {this.getSearch()}
            {this.getGroupByDropdown()}
            {this.getFilters()}
          </roche-grid>, modal,
        )}

        {isMedium && <roche-grid layout='12' full-width={true}>
          <roche-pipeline-table
            is-small={isSmall}
            group-list-by={groupListBy}
            content={JSON.stringify(content)}
          ></roche-pipeline-table>

          <roche-button
            text="Filter"
            appearance='bottom-fixed'
            is-modal-trigger="true"
            no-margin
            right-icon="filter">
          </roche-button>
        </roche-grid>

        }

        {!isMedium && <roche-grid layout='8-1-3' overflow-visible full-width={true}>
          <roche-pipeline-table
            is-small={isSmall}
            slot='left'
            group-list-by={groupListBy}
            content={JSON.stringify(content)}
          ></roche-pipeline-table>
          {/* TODO: Refactor */}
          <div slot="right" style={{
            position: 'sticky', top: '1rem', zIndex: '100',
          }}>
            <roche-text-link
              text="Download current view as CSV"
              left-icon="download"
              onClick={() => this.createAndDownloadCSV()}
            ></roche-text-link>
            {this.getSearch()}
            {this.getGroupByDropdown()}
            {this.getFilters()}
          </div>
        </roche-grid>}
      </div>
    );
  }

  private async createAndDownloadCSV(): Promise<void> {
    const data = this.state.content;
    const header = ['Compound', 'Generic Name', 'Trade name', 'Combination', 'Therapeutic area',
      'Project type', 'Indication', 'Phase', 'Filing date', 'Partner', 'Managed by', 'Description'];

    const csvRows = [
      header.join(','),
      ...data.map((item) => [
        escape(item.name),
        escape(item.genericName),
        escape(item.tradeName),
        escape(item.combination),
        // map to correct value, see EXTCWP-475
        escape(item.therapeuticArea === cardiovascularKey
          ? cardiovascularNewLabel : item.therapeuticArea),
        escape(item.projectType),
        escape(item.indicationShort),
        escape(item.originalPhase),
        escape(item.filingDate),
        escape(item.partner),
        escape(item.managedBy),
        escape(item.description),
      ],
      ).map((row) => row.join(',')),
    ];
    const csvString = csvRows.join('\n');

    const dataURL = URL.createObjectURL(new Blob([csvString], { type: 'text/csv;charset=utf-8;' }));
    const temporaryLink = document.createElement('a');
    temporaryLink.href = dataURL;
    temporaryLink.setAttribute('download', `Roche_Pipeline_Final_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(temporaryLink);
    temporaryLink.click();
    document.body.removeChild(temporaryLink);
    URL.revokeObjectURL(dataURL);
  }

  public componentWillUnmount(): void {
    this.container.current.removeEventListener('rocheFormControlChange', this.handleFiltersUpdate);
    modal.removeEventListener('rocheFormControlChange', this.handleFiltersUpdate);
    window.removeEventListener('rocheModalTrigger', this.handleModalTrigger);
    window.removeEventListener('rocheModalClose', this.handleModalClose);
  }

  private updateContent(): void {
    let content = FilterService.filterByAttributes(
      this.originalContent.map((item) => (
        { ...item }
      )),
      Array.from(this.state.filters, (filterMap) => filterMap[1]),
      ',',
    ) as PipelineEntry[];

    content = FilterService.filterBySearchText(content, this.state.searchText)
      .filter((dataEntry) => dataEntry) as PipelineEntry[]; // removes null values

    this.setState({
      content,
    });
  }

  private handleFiltersUpdate(event): void {
    const { name, value } = event?.detail;
    if (name === TypeNames.GROUP_LIST_BY) {
      this.setState({ groupListBy: value });
      return;
    }

    if (name === TypeNames.SEARCH) {
      this.setState({ searchText: value }, () => this.updateContent());
      return;
    }

    this.setState({
      filters: this.state.filters.set(
        name,
        { attributeName: name, filter: value },
      ),
    });
    this.updateContent();
  }

  private handleResize(): void {
    this.setState({ isMedium: this.isMedium, isSmall: this.isSmall });
  }

  private handleModalTrigger(): void {
    modal.setAttribute('appearance', 'pipeline');
    this.setState({ isModalOpen: true });
  }

  private handleModalClose(): void {
    modal.setAttribute('appearance', 'default');
    this.setState({ isModalOpen: false });
  }

  private getFilters(): JSX.Element {
    const { filterOptions } = this.state;
    return <roche-accordion-tabs
      appearance='pipeline'
      type="accordion"
      no-margin
    >
      {this.getFilterItem('Therapeutic Area', 'therapeuticAreaFilterValue', filterOptions.therapeuticAreaFilterValue)}
      {this.getFilterItem('Project Type', 'projectType', filterOptions.projectTypes)}
      {this.getFilterItem('Phase', 'phase', filterOptions.phases)}
      {this.getFilterItem('Expected Filing Date', 'filingDate', filterOptions.filingDates)}
      {this.getFilterItem('Managed By', 'managedBy', filterOptions.managedBy)}
    </roche-accordion-tabs>;
  }

  /* TODO:
  When rocheglobal has been updated with new icon values, return type should be SvgType
  import ProjectType from rocheglobal
  */
  // eslint-disable-next-line class-methods-use-this
  private getIcon(value: RocheSelectItem): string {
    if (value.group === FilterGroupType.THERAPEUTIC_AREA_FILTER_VALUE) {
      return 'color-indicator';
    }
    if (value.group === FilterGroupType.PROJECT_TYPE
      && value.value === ProjectType.ADDITIONAL_INDICATOR) {
      return 'additional';
    }
    if (value.group === FilterGroupType.PROJECT_TYPE
      && value.value === ProjectType.NEW_MOLECULAR_ENTITY) {
      return 'molecular-item';
    }
    return null;
  }

  private getFilterItem(headline: string, name: string, values: RocheSelectItem[]): JSX.Element {
    const selected: string = this.state.filters.get(name)?.filter as string;

    const mappedValues = values.map((value: RocheSelectItem) => ({
      ...value,
      checked: selected?.indexOf(value?.value) >= 0,
      icon: this.getIcon(value),
    }));

    return <roche-accordion-tabs-item
      headline={headline}
      appearance="pipeline"
      is-overflow-y-scrollable={mappedValues.length > 3}
    >
      <roche-select
        label-wrapper
        select-all
        multi-select
        name={name}
        options={JSON.stringify(mappedValues)}
        enable-scroll-into-view>
      </roche-select>
    </roche-accordion-tabs-item>;
  }

  private getSearch(): JSX.Element {
    return (
      <roche-input
        name="search"
        type="search"
        label="Search"
        value={this.state.searchText}
        required
      ></roche-input>);
  }

  private getGroupByDropdown(): JSX.Element {
    return (
      <roche-dropdown
        name="groupListBy"
        label="Group list by"
        selected={groupListByOptions.findIndex(
          (groupListByOption) => groupListByOption.value === this.state.groupListBy,
        )}
        required
        options={JSON.stringify(groupListByOptions)}
      ></roche-dropdown>);
  }

  // eslint-disable-next-line class-methods-use-this
  private get clientWidth(): number {
    return typeof document !== 'undefined' ? document.body.clientWidth : null;
  }

  private get isSmall(): boolean { return this.clientWidth < breakpoints.medium; }

  private get isMedium(): boolean { return this.clientWidth < breakpoints.large; }

  // eslint-disable-next-line class-methods-use-this
  private cleanupPipelineEntry(dirtyPipelineEntry): PipelineEntry {
    if (dirtyPipelineEntry.visibility === false) {
      return null;
    }
    const newEntry = dirtyPipelineEntry;

    newEntry.originalPhase = newEntry.phase;

    if (!newEntry.phase || newEntry.phase === '-') {
      newEntry.phase = '';
    }

    // TODO: if decision from Roche is there how to handle Approved phase change this.
    if (newEntry.phase === Phases.APPROVED) {
      newEntry.phase = Phases.FILED;
    }

    // Mapping therapeutic area to the correct value, see EXTCWP-475
    if (newEntry.therapeuticArea === cardiovascularOldLabel
      || newEntry.therapeuticArea === cardiovascularNewLabel) {
      newEntry.therapeuticArea = cardiovascularKey;
      newEntry.therapeuticAreaFilterValue = cardiovascularKey;
      newEntry.therapeuticAreaGroupByValue = cardiovascularNewLabel;
    } else {
      newEntry.therapeuticAreaFilterValue = newEntry.therapeuticArea.toLowerCase();
      newEntry.therapeuticAreaGroupByValue = newEntry.therapeuticArea;
    }

    return newEntry as PipelineEntry;
  }

  private updateFilterOptions(pipelineEntries: PipelineEntry[]): void {
    this.setState({
      filterOptions: {
        therapeuticAreaFilterValue: moveEntryToEnd(getOptionsByKey(pipelineEntries, FilterGroupType.THERAPEUTIC_AREA_FILTER_VALUE), 'other'),
        projectTypes: getOptionsByKey(pipelineEntries, FilterGroupType.PROJECT_TYPE),
        phases: moveEntryToEnd(getOptionsByKey(pipelineEntries, FilterGroupType.PHASE),
          Phases.FILED),
        filingDates: getOptionsByKey(pipelineEntries, FilterGroupType.EXPECTED_FILING_DATE),
        managedBy: getOptionsByKey(pipelineEntries, FilterGroupType.MANAGED_BY),
      },
    });
  }
}

function escape(value: string): string {
  return `"${value}"`;
}
